import React, { useContext, useEffect, useState } from 'react';
import { Button, Select, message, Empty, Alert, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { AppContext } from 'context/AppContext';
import { getHosts } from 'utils/apis/host';
import {
  getMarketplaceCatalog,
  getMarketplaceSubscriptions,
  subscribeMarketplaceProduct,
  unsubscribeMarketplaceProduct,
} from '../apps/owner-reporting/api/marketplace';
import { buildMarketplaceOwnerReportingUri } from 'utils/routes';
import { getProductIcon } from './productIcons';
import { resolvePreferredHostId, setMarketplaceSelectedHostId } from './selectedHost';
import styles from './Catalog.module.css';

const ProductCard = ({ product, active, hostId, onEnable, onDisable }) => {
  const icon = getProductIcon(product);
  const initial = (product.name || product.code || '?').charAt(0).toUpperCase();
  const openAppTo = hostId
    ? `${buildMarketplaceOwnerReportingUri()}?hostId=${encodeURIComponent(hostId)}`
    : buildMarketplaceOwnerReportingUri();

  return (
    <article className={styles.productCard + (active ? ' ' + styles.productCardActive : '')}>
      <div className={styles.iconStage}>
        {icon ? (
          <img className={styles.iconImage} src={icon} alt="" />
        ) : (
          <div className={styles.iconFallback}>{initial}</div>
        )}
        <span className={styles.statusBadge + ' ' + (active ? styles.statusOn : styles.statusOff)}>
          {active ? 'Enabled' : 'Available'}
        </span>
      </div>

      <div className={styles.body}>
        <h3 className={styles.name}>{product.name}</h3>
        <p className={styles.description}>{product.description || 'Marketplace application for Host Platform.'}</p>

        <div className={styles.metaRow}>
          <span className={styles.chip}>{product.version || 'v1'}</span>
          <span className={styles.chip}>{product.code}</span>
        </div>

        <div className={styles.actions}>
          {active ? (
            <span>
              {product.code === 'owner-reporting' && (
                <Link to={openAppTo}>
                  <Button type="primary" className={styles.primaryBtn}>
                    Open App
                  </Button>
                </Link>
              )}
              <Button className={styles.ghostBtn} onClick={() => onDisable(product)}>
                Disable
              </Button>
            </span>
          ) : (
            <Button type="primary" className={styles.primaryBtn} onClick={() => onEnable(product)} disabled={!hostId}>
              Enable
            </Button>
          )}
        </div>
      </div>
    </article>
  );
};

/**
 * Marketplace catalog shell — host selector + product cards with icons.
 */
const MarketplaceCatalog = () => {
  const { getUserHosts, checkIsAdmin } = useContext(AppContext);
  const allowedHostIds = (getUserHosts && getUserHosts()) || [];
  const isAdmin = !!(checkIsAdmin && checkIsAdmin());

  const [hostId, setHostId] = useState(undefined);
  const [hostOptions, setHostOptions] = useState([]);
  const [hostsLoading, setHostsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setHostsLoading(true);

    getHosts()
      .then(res => {
        if (cancelled) return;
        const hosts = (res && res.data) || [];
        let options = hosts.map(h => ({ value: String(h._id), label: h.name || String(h._id) }));

        if (!isAdmin && allowedHostIds.length > 0) {
          const allowed = allowedHostIds.map(String);
          options = options.filter(opt => allowed.includes(String(opt.value)));
        }

        setHostOptions(options);

        const preferred = resolvePreferredHostId({ options, allowedHostIds });
        setHostId(preferred);
        if (preferred) setMarketplaceSelectedHostId(preferred);
      })
      .catch(() => {
        if (cancelled) return;
        setHostOptions([]);
        if (allowedHostIds[0]) {
          const fallback = String(allowedHostIds[0]);
          setHostId(fallback);
          setHostOptions([{ value: fallback, label: 'Current host' }]);
          setMarketplaceSelectedHostId(fallback);
        }
      })
      .finally(() => {
        if (!cancelled) setHostsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const catalog = await getMarketplaceCatalog();
      const list = Array.isArray(catalog) ? catalog : [];
      setProducts(list);

      if (hostId) {
        const subs = await getMarketplaceSubscriptions(hostId);
        setSubscriptions(Array.isArray(subs) ? subs : []);
      } else {
        setSubscriptions([]);
      }

      if (!list.length) {
        setError(
          'No marketplace products found. Restart nebulapi so manifests sync (look for "[marketplace] Synced" in API logs), then refresh.'
        );
      }
    } catch (e) {
      setProducts([]);
      setSubscriptions([]);
      setError((e && e.message) || 'Failed to load marketplace');
      message.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hostsLoading) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId, hostsLoading]);

  const isActive = product =>
    subscriptions.some(
      s => s.productCode === product.code && s.productVersion === (product.version || 'v1') && s.status === 'active'
    );

  const onEnable = async product => {
    if (!hostId) {
      message.error('Select a host first');
      return;
    }
    try {
      await subscribeMarketplaceProduct({
        hostId,
        productCode: product.code,
        productVersion: product.version || 'v1',
      });
      message.success((product.name || product.code) + ' enabled for selected host');
      load();
    } catch (e) {
      message.error((e && e.message) || 'Enable failed');
    }
  };

  const onDisable = async product => {
    if (!hostId) {
      message.error('Select a host first');
      return;
    }
    try {
      await unsubscribeMarketplaceProduct({
        hostId,
        productCode: product.code,
        productVersion: product.version || 'v1',
      });
      message.success((product.name || product.code) + ' disabled for selected host');
      load();
    } catch (e) {
      message.error((e && e.message) || 'Disable failed');
    }
  };

  const selectedHostLabel = (hostOptions.find(o => String(o.value) === String(hostId)) || {}).label;

  return (
    <div className={styles.page}>
      <section className={styles.heroBar}>
        <h1 className={styles.heroTitle}>Marketplace</h1>
        <p className={styles.heroSub}>
          Discover Host Platform apps for the selected host. Enable a product to unlock its pages, unit slots, and
          booking event actions.
        </p>
        <div className={styles.hostRow}>
          <span className={styles.hostLabel}>Host</span>
          {hostsLoading ? (
            <Spin size="small" />
          ) : (
            <Select
              showSearch
              className={styles.hostSelect}
              placeholder="Select host"
              value={hostId}
              onChange={value => {
                setHostId(value);
                setMarketplaceSelectedHostId(value);
              }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option.props.children)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              notFoundContent="No hosts found"
            >
              {hostOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          )}
          <span className={styles.hostPill}>
            {hostId ? 'Managing: ' + (selectedHostLabel || hostId) : 'Select a host to enable apps'}
          </span>
        </div>
      </section>

      <section className={styles.appsSection}>
        <h2 className={styles.appsHeading}>Apps</h2>
        <p className={styles.appsHint}>
          {hostId
            ? 'Enable an app for this host, then open it from the card actions.'
            : 'Choose a host above to manage app subscriptions.'}
        </p>

        {error ? <Alert className={styles.alert} type="warning" showIcon message={error} /> : null}

        {loading || hostsLoading ? (
          <div className={styles.emptyWrap}>
            <Spin />
          </div>
        ) : !hostId ? (
          <div className={styles.emptyWrap}>
            <Empty description="Choose a host above to view and manage marketplace apps" />
          </div>
        ) : products.length === 0 ? (
          <div className={styles.emptyWrap}>
            <Empty description="No marketplace apps available yet" />
          </div>
        ) : (
          <div className={styles.grid}>
            {products.map(product => (
              <div className={styles.gridItem} key={product.code + '-' + (product.version || 'v1')}>
                <ProductCard
                  product={product}
                  active={isActive(product)}
                  hostId={hostId}
                  onEnable={onEnable}
                  onDisable={onDisable}
                />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default MarketplaceCatalog;
